import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import './Request.css';

const Request = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [requestType, setRequestType] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [questions, setQuestions] = useState(['', '', '', '', '']);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);

  const { accessToken, refreshAccessToken } = useAuth();
  const navigate = useNavigate();

  // Helper to ensure valid token
  const getValidAccessToken = async () => {
    try {
      if (!accessToken) {
        const newAccessToken = await refreshAccessToken();
        return newAccessToken;
      }
      return accessToken;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      setStatusMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      navigate('/login');
      return null;
    }
  };

  // Fetch company ID
  const fetchCompanyId = async () => {
    try {
      const validToken = await getValidAccessToken();
      if (!validToken) return null;

      const response = await api.post(
        '/server/companies/company',
        {},
        {
          headers: { Authorization: `Bearer ${validToken}` },
        }
      );

      return response.data.id;
    } catch (error) {
      console.error('Error fetching company ID:', error);
      setStatusMessage({ type: 'error', text: 'Error fetching company information.' });
      return null;
    }
  };

  // Clear form fields
  const clearFormFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRequestType('');
    setPositionTitle('');
    setQuestions(['', '', '', '', '']);
    setResumeFile(null);
    setError('');
  };

  // Add candidate and send email
  const addCandidate = async (candidatePreScreenDTO, requestType, resumeFile) => {
    let successTimer;
    let errorTimer;
    
    try {
      // 1. Validate and prepare data
      const companyId = await fetchCompanyId();
      if (!companyId) {
        throw new Error('Unable to identify company');
      }
  
      const validToken = await getValidAccessToken();
      if (!validToken) {
        throw new Error('Authentication required');
      }
  
      // 2. Prepare form data
      const formData = new FormData();

      console.log('Raw candidatePreScreenDTO:', candidatePreScreenDTO);
      
      
      formData.append(
        'candidatePreScreenDTO', 
        new Blob([JSON.stringify(candidatePreScreenDTO)], { type: 'application/json' })
      );
  
      if (requestType === 'prescreen' && resumeFile) {
        formData.append('resumePdf', resumeFile);
      }

      console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }


      // Debug what's being sent
    console.log('Submitting:', {
      companyId,
      requestType,
      data: candidatePreScreenDTO, 
      hasResume: !!resumeFile,
      headers: {
        Authorization: `Bearer ${validToken}`,
        'Content-Type': 'multipart/form-data',
      }
    });
  
      // 3. Submit candidate data
      const addCandidateResponse = await api.post(
        `/server/companies/${companyId}/addCandidate?requestType=${requestType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${validToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
  
      // Check for specific status codes and handle them
      if (addCandidateResponse.status === 400) {
        throw new Error(addCandidateResponse.data || 'Invalid request data');
      }
  
      if (!addCandidateResponse.data) {
        throw new Error('No response data received from server');
      }
  
      // 4. Send confirmation email (only if candidate addition was successful)
      const candidateEmail = candidatePreScreenDTO.email;
      const params = new URLSearchParams({
        candidateEmail,
        companyId,
        requestType,
      });
  
      const emailResponse = await api.get(`/email/send-email?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${validToken}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (emailResponse.status !== 200) {
        throw new Error(emailResponse.data?.message || 'Email service failed');
      }
  
      // 5. Handle success
      setStatusMessage({
        type: 'success',
        text: 'Candidate added and email sent successfully! You will be redirected shortly...',
      });
      clearFormFields();
  
      successTimer = setTimeout(() => {
        navigate('/userdashboard', {
          state: { 
            recentAction: 'candidateAdded',
            candidateName: `${candidatePreScreenDTO.firstName} ${candidatePreScreenDTO.lastName}`
          }
        });
      }, 3000);
  
      return () => {
        if (successTimer) clearTimeout(successTimer);
      };
  
    } catch (error) {
      // Enhanced error logging
      console.error('Full error details:', {
        error,
        response: error.response,
        request: error.request,
        config: error.config,
        stack: error.stack
      });
  
      let errorMessage = 'Submission failed';
      
      if (error.response) {
        // More detailed error message construction
        errorMessage = `Server responded with ${error.response.status}: ${error.response.data?.message || 'No error details provided'}`;
        
        // Log the response data if available
        if (error.response.data) {
          console.error('Error response data:', error.response.data);
        }
      } else if (error.request) {
        errorMessage = 'No response received from server';
        console.error('Request was made but no response received:', error.request);
      } else if (error.message) {
        errorMessage = error.message;
      }
  
      setStatusMessage({
        type: 'error',
        text: errorMessage,
      });
  
      errorTimer = setTimeout(() => {
        setStatusMessage({ type: '', text: '' });
      }, 5000);
  
      return () => {
        if (errorTimer) clearTimeout(errorTimer);
      };
    } finally {
      setLoading(false);
    }
  };

  // Handle question change
  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  // Handle key down for questions
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' && index < questions.length - 1) {
      document.getElementById(`question-${index + 1}`).focus();
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !(file.type === 'application/pdf' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      setError('Please upload a PDF or DOCX file.');
      setResumeFile(null);
    } else if (file && file.size > 5 * 1024 * 1024) {
      setError('File size should not exceed 5MB.');
      setResumeFile(null);
    } else {
      setResumeFile(file);
      setError('');
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
    // Validate required fields
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !requestType) {
      setStatusMessage({ 
        type: 'error', 
        text: 'Please fill all required fields' 
      });
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      setLoading(false); 
      return;
    }
  
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setStatusMessage({
        type: 'error',
        text: 'Please enter a valid email address'
      });
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      setLoading(false); 
      return;
    }

    
    if (phone && phone.trim().length !== 10) {
      setStatusMessage({
        type: 'error',
        text: 'Phone number must be exactly 10 digits if provided'
      });
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      setLoading(false); 
      return;
    }
  
    setLoading(true); 
  
    try {
      const trimmedRequestType = requestType.trim().toLowerCase();
      if (!['reference', 'prescreen'].includes(trimmedRequestType)) {
        throw new Error('Please select a valid service type'); 
      }
  
      
      const candidatePreScreenDTO = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: phone.trim() || '', 
        // Conditionally add fields based on requestType
        ...(trimmedRequestType === 'prescreen' && {
          positionTitle: positionTitle.trim(),
          questions: questions.map(q => q.trim()),
        }),
        
      };
  
      await addCandidate(candidatePreScreenDTO, trimmedRequestType, resumeFile);
      window.scrollTo(0, 0); 
  
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: error.message || 'An error occurred during submission'
      });
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition); 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="request-demo">
      <main className="request-demo-main">
        <section className="hero-section">
          <div className="demo-container">
            <div className="demo-form">
              <h4 className="highlighted-header">Candidate Reference Request Form</h4>
              {statusMessage.text && (
                <div
                  className={`status-message ${
                    statusMessage.type === 'success' ? 'success' : 'error'
                  }`}
                >
                  {statusMessage.text}
                </div>
              )}
              {!statusMessage.text && (
                <form className="candidate-info" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="firstName" className="custom-label">
                      Candidate First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName" className="custom-label">
                      Candidate Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="custom-label">
                      Candidate Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="custom-label">
                      Candidate Phone Number 
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        if (value === '' || /^\d*$/.test(value)) { 
                            setPhone(value);
                        }
                      }}
                      
                    />
                  </div>

                  {/* Select Service Type Section */}
                  <div className="form-group">
                    <label htmlFor="referenceType" className="custom-label">Select Service Type: <span className="required">*</span></label>
                    <select
                      id="referenceType"
                      className="dropdown"
                      value={requestType}
                      onChange={(e) => {
                        setRequestType(e.target.value);
                        setError(''); 
                      }}
                      required
                    >
                      <option value="">-- Select Service Type --</option>
                      <option value="reference"> Reference</option>
                      <option value="prescreen">Prescreen</option>
                    </select>
                    {error && <span className="error-message">{error}</span>}
                  </div>

                  {/* Pre-screening Form (Conditional Rendering) */}
                  {requestType === 'prescreen' && (
                    <div className="pre-screening-container">
                      <h3 className="custom-label"> Pre-Screening Questionnaire Form</h3>
                      <div className="form-group">
                        <label className="custom-label">Position Title Opening:</label>
                        <input
                          type="text"
                          value={positionTitle}
                          onChange={(e) => setPositionTitle(e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="custom-label">
                          Please provide the priority questions (up to five) that you would like us to ask:
                        </label>
                        {questions.map((question, index) => (
                          <textarea
                            key={index}
                            id={`question-${index}`}
                            type="text"
                            value={question}
                            onChange={(e) => handleQuestionChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            placeholder={`Question ${index + 1}`}
                            className="custom-question-textarea"
                          />
                        ))}
                      </div>

                      {/* Resume Upload Section (Only for Pre-screening) */}
                      <div className="form-group">
                        <label htmlFor="resume" className="custom-label">
                          Upload Candidate's Resume (PDF or DOCX only, Optional):
                        </label>
                        <input
                          type="file"
                          id="resume"
                          accept=".pdf,.docx"
                          onChange={handleFileChange}
                        />
                        {error && <span className="error-message">{error}</span>}
                      </div>
                    </div>
                  )}

                  {/* Submit Request Button */}
                  <button type="submit" className="request-demo-button" disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Request;